/**
 * Payment — a settled inflow of money against a Loan.
 *
 * MVP Payment is mostly an audit record. The ledger entries that result
 * from a payment (debit cash_collected_<provider>, credit principal /
 * interest / fees per loan policy) are produced by `applyPayment` in
 * step 6 and persisted as `LedgerEntry` rows (see ADR-0003 outbox + plan
 * P1.A.2 ledger schema).
 *
 * Status machine:
 *   pending → settled                    (provider confirmed and reconciled)
 *   pending → failed                     (rejected at provider)
 *   settled → refunded                   (refund flow)
 *
 * pending / failed / refunded are not "terminal" in the loan-state sense —
 * a refund itself produces a reversing Payment, not a transition on the
 * original.
 */
import { z } from "zod";
const moneySchema = z.object({
    amountMinor: z.number().int().positive(),
    currency: z.string().length(3),
});
export const paymentSchema = z.object({
    id: z.string().uuid(),
    countryId: z.string().uuid(),
    loanId: z.string().uuid(),
    intentId: z.string().uuid().optional(),
    channel: z.string().min(1),
    amount: moneySchema,
    providerReference: z.string().min(1),
    status: z.enum(["pending", "settled", "failed", "refunded"]),
    settledAt: z.coerce.date().optional(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
});
export function makePayment(raw) {
    const parsed = paymentSchema.parse(raw);
    return {
        id: parsed.id,
        countryId: parsed.countryId,
        loanId: parsed.loanId,
        ...(parsed.intentId !== undefined ? { intentId: parsed.intentId } : {}),
        channel: parsed.channel,
        amount: {
            amountMinor: parsed.amount.amountMinor,
            currency: parsed.amount.currency,
        },
        providerReference: parsed.providerReference,
        status: parsed.status,
        ...(parsed.settledAt !== undefined ? { settledAt: parsed.settledAt } : {}),
        createdAt: parsed.createdAt,
        updatedAt: parsed.updatedAt,
    };
}
//# sourceMappingURL=payment.js.map